import { handleError } from "@/api/handleError";
import { formatNigerianNumber, isNigerianNumber } from "@/helpers/utils";
import { useBusinessStore } from "@/stores/businesses.store";
import { useDialerV2Store } from "@/stores/dialerV2.store";
import { useNumberStore } from "@/stores/numbers.store";
import { useUtilsStore } from "@/stores/utils.store";
import { notify } from "@kyvg/vue3-notification";
import { PressOneOptions } from "@pressone/dialer/lib/types";
import { useSipCredentials } from "./useSipCredentials";

export function useDialer() {
  const dialerStore = useDialerV2Store();
  const numberStore = useNumberStore();
  const utilsStore = useUtilsStore();
  const businessStore = useBusinessStore();

  const sipCredentialsComposable = useSipCredentials();

  // Initialize the dialer with SIP credentials
  const initialize = async (options?: Partial<PressOneOptions>) => {
    if (dialerStore.initializationState.isInitialized) {
      console.warn("Dialer already initialized");

      return;
    }

    try {
      // Check if user has restrictions
      if (!dialerStore.canMakeCall) {
        throw new Error("Connection restricted due to cancelled subscription");
      }

      // Check online status
      if (!navigator.onLine) {
        throw new Error(
          "You are currently not online. Kindly connect to the internet to make calls"
        );
      }

      // Fetch SIP credentials
      const rawCredentials = await sipCredentialsComposable.fetchCredentials();

      // Map credentials to business numbers
      const mappedCredentials =
        sipCredentialsComposable.mapCredentialsToNumbers(
          rawCredentials,
          numberStore.numbers || []
        );

      if (Object.keys(mappedCredentials).length === 0) {
        throw new Error("No valid SIP credentials found");
      }

      // Initialize the dialer
      dialerStore.initializeDialer(mappedCredentials, options);
    } catch (error) {
      dialerStore.setInitializationError("Failed to initialize dialer");
      throw error;
    }
  };

  // Start the dialer with specific instance keys
  const start = async (defaultInstanceKey?: string) => {
    try {
      const instanceKey =
        defaultInstanceKey || numberStore.currentUserReceiverKey?.toString();

      if (!instanceKey) {
        throw new Error("No default instance key available");
      }

      // Map credentials to business numbers
      const mappedCredentials = dialerStore.sipCredentials;

      // Get all available instance keys
      const allInstanceKeys = Object.keys(mappedCredentials);

      if (allInstanceKeys.length === 0) {
        throw new Error("No valid SIP credentials found");
      }

      await dialerStore.startDialer(instanceKey, allInstanceKeys);
    } catch (error) {
      dialerStore.setInitializationError("Failed to start dialer");
      throw error;
    }
  };

  // Stop the dialer
  const stop = async (defaultInstanceKeysToStop?: string[]) => {
    if (!dialerStore.initializationState.isInitialized) {
      return;
    }

    const instanceKeysToStop =
      defaultInstanceKeysToStop ||
      Object.keys(dialerStore.sipCredentials || {});

    if (instanceKeysToStop.length === 0) {
      throw new Error("No instance keys to stop");
    }

    await dialerStore.stopDialer(instanceKeysToStop);
  };

  const reconnectPhone = async (id?: number | string | null) => {
    // Validate that the instance key exists and has valid credentials
    // Only numbers with valid credentials are stored in sipCredentials
    const idString = id?.toString();

    if (!idString || !dialerStore.sipCredentials?.[idString]) {
      handleError({
        error: new Error("Unable to reconnect phone, please try again"),
        silent: true,
      });

      return;
    }

    // Check if dialer is initialized
    if (!dialerStore.initializationState.isInitialized) {
      handleError({
        error: new Error("Dialer not initialized. Please initialize first."),
        silent: true,
      });

      return;
    }

    // Check for payment restrictions
    if (utilsStore.showDisconnectionPrompt) {
      notify({
        type: "error",
        text: "Account restricted due to outstanding payment",
      });

      return;
    }

    try {
      // Use the new dialer implementation to reconnect the specific instance
      const defaultInstanceKey = numberStore.currentUserReceiverKey?.toString();

      if (!defaultInstanceKey) {
        throw new Error("No default instance key available for reconnection");
      }

      // Reconnect only the specific instance by calling startDialer with specific keys
      await dialerStore.startDialer(defaultInstanceKey, [idString]);
    } catch (error) {
      handleError({
        error: new Error(`Failed to reconnect phone: ${String(error)}`),
      });
    }
  };

  // Make an outgoing call
  const call = async ({
    number,
    instanceKey,
    callerIdNumber,
    skipNumberValidation,
    callTriggeredBy,
  }: {
    number?: string;
    instanceKey?: string;
    callerIdNumber?: string;
    skipNumberValidation?: boolean;
    callTriggeredBy?: "regular_call" | "outbound_call_queue";
  }) => {
    // Guard: Check if dialer is initialized
    if (!dialerStore.initializationState.isInitialized) {
      notify({
        type: "error",
        text: "Dialer not initialized. Please try again.",
      });

      return;
    }

    // Guard: Check if number is provided
    if (!number || number.trim() === "") {
      notify({
        type: "error",
        text: "Please enter a valid phone number",
      });

      return;
    }

    if (!skipNumberValidation) {
      if (number.length < 9) {
        notify({
          text: "Please enter a valid phone number",
          type: "error",
        });
        return;
      }

      const activeNumberCountryCode =
        numberStore.activeNumber?.business_number?.country_code.toLowerCase();

      if (number.startsWith("+234") && activeNumberCountryCode === "ng") {
        number = "0" + number.slice(4);
      } else if (number.startsWith("234") && activeNumberCountryCode === "ng") {
        number = "0" + number.slice(3);
      }

      if (
        // if pressone users are using international numbers to call it must be in country code format
        activeNumberCountryCode !== "ng"
      ) {
        if (!isNigerianNumber(number) && !number.startsWith("+")) {
          notify({
            text: "Please ensure you add (+) with your country code before dialing.",
            type: "error",
          });
          return;
        } else {
          // Add +234 to the number if the calling number is not a Nigerian number
          number = formatNigerianNumber(number);
        }
      }
    }

    // Business logic validation: Check for unverified business restrictions
    if (
      !businessStore.activeProfile?.business?.verified &&
      utilsStore.hasMadeACall
    ) {
      notify({
        type: "error",
        text: "Please verify your business to continue making calls",
      });

      return;
    }

    try {
      const callerId =
        callerIdNumber ||
        numberStore.activeNumber?.business_number?.phone_number;

      dialerStore.setCallTriggeredBy(callTriggeredBy || "regular_call");

      await dialerStore.makeCall({
        number,
        instanceKey,
        callerIdNumber: callerId,
      });
    } catch (error) {
      handleError({ error });
    }
  };

  // Handle network status changes
  const handleOnlineStatusChange = async () => {
    try {
      if (navigator.onLine) {
        // Reconnect when coming back online

        const anySipConnected = Object.values(
          dialerStore.sipConnectionState
        ).some((status) => status.isConnected);

        if (dialerStore.initializationState.isInitialized && !anySipConnected) {
          try {
            await start();
          } catch (error) {
            console.error("Failed to reconnect dialer:", error);
          }
        }
      } else {
        // Handle going offline
        if (dialerStore.initializationState.isInitialized) {
          await stop();
        }
      }
    } catch (error) {
      handleError({ error, silent: true });
    }
  };

  // Setup network event listeners
  const setupNetworkListeners = () => {
    window.addEventListener("online", () => {
      void handleOnlineStatusChange();
    });
    window.addEventListener("offline", () => {
      void handleOnlineStatusChange();
    });
  };

  // Cleanup network event listeners
  const cleanupNetworkListeners = () => {
    window.removeEventListener("online", () => {
      void handleOnlineStatusChange();
    });
    window.removeEventListener("offline", () => {
      void handleOnlineStatusChange();
    });
  };

  return {
    initialize,
    start,
    stop,
    reconnectPhone,
    call,
    setupNetworkListeners,
    cleanupNetworkListeners,

    // Expose dialer store for direct access
    dialer: dialerStore,
  };
}
